<template>
  <div>
    <Head title="បង់ប្រាក់" />
    <h1 class="moul flex items-center mb-8 text-2xl">
      <Link class="flex items-center text-indigo-400 hover:text-indigo-600" href="/loans"><BanknotesIcon class="mr-2 w-8 h-8" /> បង់ប្រាក់</Link>
      <span class="text-indigo-400 font-medium"><ChevronRightIcon class="w-5 h-5" /></span> សម្រាប់ {{ form.customer_fullname }}
    </h1>
    <div v-if="this.paids.length" class="mb-10 p-8 bg-white rounded-md shadow overflow-hidden">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <td class="pb-4 pt-4 px-6">កាលបរិច្ឆេទបង់ប្រាក់</td>
          <td class="pb-4 pt-4 px-6">ទឹកប្រាក់បានបង់</td>
        </tr>
        <tr v-for="pay in this.paids" :key="pay.id" class="text-left border-t">
          <td class="pb-4 pt-4 px-6">{{ formattedDate(pay.paid_date) }}</td>
          <td class="pb-4 pt-4 px-6">{{ formatNumber(pay.paid_amount) }}</td>
        </tr>
        <tr class="text-left font-medium border-t">
          <td class="pb-4 pt-4 px-6 text-right text-indigo-400">ប្រាក់ដែលបានបង់ពីមុន៖</td>
          <td class="pb-4 pt-4 px-6 text-indigo-400">{{ formatNumber(totalPaidAmount) }}</td>
        </tr>
      </table>
    </div>
    <div class="bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="store">
        <div class="flex flex-wrap p-8">
          <text-input v-model="form.customer_id" placeholder="វាយ ID អតិថិជនដែលមានស្រាប់" :error="form.errors.customer_id" class="pb-8 pr-6 w-full lg:w-1/4" label="ID អតិថិជន *" @blur="handleCustomerID($event)" />
          <div class="relative pr-6 w-full lg:w-1/4">
            <text-input v-model="form.customer_fullname" @keydown="handleCustomer($event)" :error="form.errors.customer_fullname" class="w-full" placeholder="វាយឈ្មោះ អតិថិជនដែលមានស្រាប់" label="ឈ្មោះពេញអិតិថិជន *" />
            <div v-if="this.suggested_customers.length > 0" style="position: absolute; z-index: 100" class="autocomplete-dropdown absolute z-10 -mt-8 w-full bg-white border rounded shadow-lg">
              <div v-for="suggest in this.suggested_customers" :key="suggest.id" class="p-2 text-indigo-600 hover:bg-gray-100 cursor-pointer" @click="setCustomer(suggest)">{{ suggest.fullname }} - {{ suggest.customer_id }}</div>
            </div>
          </div>
          <text-input v-model="form.loan_id" :error="form.errors.loan_id" class="pb-8 pr-6 w-full lg:w-1/4" label="ID កម្ចី *" />
          <text-input disabled v-model="form.pay_date" :error="form.errors.pay_date" class="pb-8 pr-6 w-full lg:w-1/4" label="កាលបរិច្ឆេទបង់ប្រាក់ *" />
          <text-input v-if="form.pay_id" disabled v-model="form.balance" :error="form.errors.balance" class="pb-8 pr-6 w-full lg:w-1/4" label="ប្រាក់ដើមនៅសល់ *" />
          <div v-if="form.can_pay_all" class="pb-4 w-full text-red-500 font-medium lg:pt-8 lg:w-3/4">
            <Link :href="`/repays/${form.pay_id}/payall`">ចុចទីនេះដើម្បីបង់ផ្តាច់?</Link>
          </div>
          <span class="w-full pb-3 text-orange-500 font-bold" v-if="form.late_days">យឺតចំនួន {{ form.late_days }} ថ្ងៃ<hr></span>
          <text-input v-if="form.pay_id" disabled v-model="form.principle_amount" :error="form.errors.principle_amount" class="pb-8 pr-6 w-full lg:w-1/4" label="ប្រាក់ដើមត្រូវសង *" />
          <text-input v-if="form.pay_id" disabled v-model="form.interest" :error="form.errors.interest" class="pb-8 pr-6 w-full lg:w-1/4" label="ការប្រាក់ត្រូវសង *" />
          <text-input v-if="form.pay_id" disabled v-model="form.service_fee" :error="form.errors.service_fee" class="pb-8 pr-6 w-full lg:w-1/4" label="សេវាកម្មប្រ/តិ និងរដ្ឋបាល *" />
          <text-input v-if="form.pay_id && this.totalPaidAmount <= 0" type="number" step="0.01" @keyup="handlePenalty($event)" v-model="form.penalty" :error="form.errors.penalty" class="pb-8 pr-6 w-full lg:w-1/4" label="ពិន័យ" />
          <text-input v-if="form.pay_id && this.totalPaidAmount > 0" disabled type="number" step="0.01" v-model="form.penalty" :error="form.errors.penalty" class="pb-8 pr-6 w-full lg:w-1/4" label="ពិន័យ" />
          <text-input v-if="form.pay_id" disabled v-model="form.amount_to_pay" :error="form.errors.amount_to_pay" class="pb-8 pr-6 w-full lg:w-1/4" label="ត្រូវបង់សរុប *" />
          <text-input disabled v-if="form.pay_id" type="number" step="0.01" v-model="form.paid_before" :error="form.errors.paid_before" class="pb-8 pr-6 w-full lg:w-1/4" label="បានបង់ពីមុន *" />
          <text-input type="date" v-model="form.paid_date" :error="form.errors.paid_date" class="pb-8 pr-6 w-full lg:w-1/4" label="កាលបរិច្ឆេទបានបង់ *" />
          <text-input v-if="form.pay_id" @keyup="handlePaid($event)" type="number" step="0.01" v-model="form.paid_amount" :error="form.errors.paid_amount" class="pb-8 pr-6 w-full lg:w-1/4" label="ប្រាក់ដែលអាចបង់ *" />
          <text-input v-if="form.pay_id" disabled v-model="form.amount_due" :error="form.errors.amount_due" class="pb-8 pr-6 w-full lg:w-1/4" label="នៅសល់ *" />
        </div>
        <div class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100">
          <loading-button :loading="form.processing" class="btn-indigo" type="submit">ធ្វើការបង់ប្រាក់</loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import axios from 'axios'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import TextareaInput from '@/Shared/TextareaInput'
import LoadingButton from '@/Shared/LoadingButton'
import { BanknotesIcon, ChevronRightIcon } from '@heroicons/vue/24/outline'
import { formattedDate, isDateLaterThanToday, formatTwoDigits, formatNumber, lateDays } from '@/helper'

export default {
  components: {
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
    TextareaInput,
    BanknotesIcon,
    ChevronRightIcon,
  },
  layout: Layout,
  remember: 'form',
  data() {
    return {
      suggested_customers: [],
      amount_to_pay: 0,
      paids: [],
      form: this.$inertia.form({
        customer_id: null,
        customer_fullname: null,
        loan_id: null,
        loan_primary_id: null,
        loan_term: null,
        order_number: null,
        pay_date: null,
        pay_id: null,
        principle_amount: null,
        interest: null,
        service_fee: null,
        penalty: null,
        amount_to_pay: null,
        paid_amount: null,
        amount_due: null,
        paid_before: null,
        balance: null,
        paid_date: null,
        can_pay_all: false,
        is_pay_all: false,
        late_days: 0,
      }),
    }
  },
  computed: {
    totalPaidAmount() {
      return this.paids.reduce((acc, pay) => acc + parseFloat(pay.paid_amount), 0)
    },
  },

  methods: {
    async store() {
      await this.form.post('/repays', {
        onSuccess: (response) => {
          if (response.props.success !== null) {
            this.form.customer_id = null
            this.form.loan_id = null
            this.form.loan_primary_id = null
            this.form.loan_term = null
            this.form.pay_date = null
            this.form.pay_id = null
            this.form.principle_amount = null
            this.form.interest = null
            this.form.service_fee = null
            this.form.penalty = null
            this.form.amount_to_pay = null
            this.form.paid_amount = null
            this.form.amount_due = null
            this.paids = response.props.payable
            this.form.can_pay_all = null
            this.form.paid_date = null
            this.form.late_days = 0
            // console.log(response.props.success)
          }
        },
      })
    },
    formattedDate,
    formatNumber,
    handleCustomerID(e) {
      this.form.customer_fullname = null
      this.form.loan_id = null
      this.form.loan_primary_id = null
      this.form.pay_date = null
      this.form.pay_id = null
      this.form.principle_amount = null
      this.form.interest = null
      this.form.service_fee = null
      this.form.penalty = null
      this.form.amount_to_pay = null
      this.form.paid_amount = null
      this.form.amount_due = null
      this.form.balance = null
      this.form.can_pay_all = null
      this.form.late_days = 0
      this.paids = []
      axios
        .get('/repays/customer', {
          params: {
            customer_id: this.form.customer_id ? this.form.customer_id : e.target.value,
          },
        })
        .then((response) => {
          if (Object.keys(response.data).length !== 0) {
            this.paids = response.data.payable
            this.form.paid_before = this.totalPaidAmount

            this.form.customer_fullname = response.data.customer.first_name + ' ' + response.data.customer.last_name
            this.form.loan_id = response.data.loan.loan_id
            this.form.loan_primary_id = response.data.loan.id
            this.form.loan_term = response.data.loan.term
            this.form.pay_date = formattedDate(response.data.payment.pay_date)
            this.form.pay_id = response.data.payment.id
            this.form.balance = response.data.payment.balance
            this.form.order_number = response.data.payment.order_number
            this.form.principle_amount = response.data.payment.principle_amount
            this.form.interest = response.data.payment.interest
            this.form.service_fee = response.data.payment.service_fee
            this.form.penalty = (lateDays(response.data.payment.pay_date)-3)>=0 && !this.totalPaidAmount?
              parseFloat((lateDays(response.data.payment.pay_date)-2)*(response.data.payment.penalty>0?response.data.payment.penalty:0)):0
              //isDateLaterThanToday(response.data.payment.pay_date) && !this.totalPaidAmount ? response.data.payment.penalty : 0
            this.amount_to_pay = parseFloat(response.data.payment.final_pay) > 0 ? response.data.payment.final_pay : response.data.payment.amount_to_pay
            this.form.amount_to_pay = parseFloat(response.data.payment.final_pay) > 0 ? response.data.payment.final_pay : formatTwoDigits(parseFloat(this.amount_to_pay) + parseFloat(this.form.penalty))

            this.form.amount_due = formatTwoDigits(parseFloat(this.amount_to_pay) + parseFloat(this.form.penalty) - parseFloat(this.totalPaidAmount))
            this.form.paid_amount = 0
            this.form.can_pay_all = 1 //response.data.payment.can_pay_all
            this.form.late_days = lateDays(response.data.payment.pay_date)+1;
          }
        })
    },
    handlePenalty(e) {
      const penalty = e.target.value ? e.target.value : 0
      this.form.amount_to_pay = formatTwoDigits(parseFloat(this.amount_to_pay) + parseFloat(penalty))
      this.form.amount_due = formatTwoDigits(parseFloat(this.amount_to_pay) + parseFloat(penalty) - parseFloat(this.form.paid_amount) - parseFloat(this.totalPaidAmount))
    },
    handlePaid(e) {
      const paid_amount = e.target.value ? e.target.value : 0
      this.form.amount_due = formatTwoDigits(parseFloat(this.amount_to_pay) + parseFloat(this.form.penalty) - parseFloat(paid_amount) - parseFloat(this.totalPaidAmount))
    },
    handleCustomer(e) {
      this.suggested_customers = []
      if (e.target.value.length > 2) {
        axios
          .get('/contacts/find_customers', {
            params: {
              name: e.target.value,
            },
          })
          .then((response) => {
            this.suggested_customers = []
            if (Object.keys(response.data).length !== 0) {
              this.suggested_customers = response.data
            }
          })
      }
    },
    setCustomer(suggest) {
      this.form.customer_fullname = suggest.fullname
      this.form.customer_id = suggest.customer_id
      this.handleCustomerID(suggest)
      this.suggested_customers = []
    },
  },
}
</script>
